<template>
	<div
		class="group relative bg-white border border-blue-100 rounded-xl shadow-sm hover:shadow-lg hover:border-blue-300 transition-all duration-300 cursor-pointer overflow-hidden w-full hover:-translate-y-1"
	>
		<!-- Status Indicator Bar -->
		<div
			:class="[
				'h-2 w-full',
				getStatusIndicatorClass(invoice.invoiceStatus),
			]"
		></div>

		<!-- Main Content -->
		<div class="p-4 sm:p-5 space-y-4">
			<!-- Header: Date and Status -->
			<div class="flex items-start justify-between gap-3">
				<div class="min-w-0 flex-1">
					<p class="font-semibold text-gray-900 text-sm sm:text-base">
						{{ formatDate(invoice.createdDate) }}
					</p>
					<p
						class="text-xs sm:text-sm text-gray-600 mt-1"
						v-if="invoice.invoiceCategory"
					>
						{{ invoice.invoiceCategory }}
					</p>
				</div>

				<!-- Status Badge -->
				<div class="flex-shrink-0">
					<span
						:class="[
							'inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border',
							getStatusClass(invoice.invoiceStatus),
						]"
					>
						<div
							:class="[
								'w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full flex-shrink-0',
								getStatusDotClass(invoice.invoiceStatus),
							]"
						></div>
						<span class="hidden xs:inline">{{
							formatStatus(invoice.invoiceStatus)
						}}</span>
						<span class="xs:hidden">{{
							formatStatusShort(invoice.invoiceStatus)
						}}</span>
					</span>
				</div>
			</div>

			<!-- Amount Section -->
			<div
				class="bg-blue-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-blue-100"
			>
				<div class="flex items-center justify-between">
					<span class="text-xs sm:text-sm font-medium text-blue-700"
						>Total Amount</span
					>
					<div class="flex items-baseline gap-1 sm:gap-2">
						<span
							class="text-xs sm:text-sm font-medium text-blue-600"
						>
							{{ invoice.invoiceCurrency || 'KES' }}
						</span>
						<span
							class="text-lg sm:text-xl font-bold text-blue-900"
						>
							{{ formatAmount(invoice.invoiceAmount) }}
						</span>
					</div>
				</div>

				<!-- Due Date if available -->
				<div
					v-if="invoice.dueDate"
					class="flex items-center justify-between mt-2 pt-2 border-t border-blue-200/50"
				>
					<span class="text-xs text-blue-600">Due Date</span>
					<span class="text-xs font-medium text-blue-800">
						{{ formatDate(invoice.dueDate) }}
					</span>
				</div>
			</div>

			<!-- Reference Section -->
			<div
				class="flex flex-col xs:flex-row xs:items-center xs:justify-between gap-2"
				v-if="invoice.invoiceSystemRef"
			>
				<span class="text-xs font-medium text-gray-500 flex-shrink-0"
					>Reference</span
				>
				<span
					class="text-xs text-gray-700 font-mono bg-gray-50 px-2 sm:px-3 py-1.5 rounded-lg border border-gray-200 break-all min-w-0 text-right"
				>
					{{ truncateReference(invoice.invoiceSystemRef) }}
				</span>
			</div>

			<!-- Action Hover Overlay -->
			<div
				class="absolute inset-0 bg-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl pointer-events-none"
			></div>

			<!-- View Details Hint -->
			<div
				class="flex items-center justify-center pt-3 border-t border-gray-100"
			>
				<span
					class="text-xs text-gray-500 group-hover:text-blue-600 transition-colors duration-300 flex items-center gap-1.5"
				>
					<svg
						class="w-3 h-3 sm:w-3.5 sm:h-3.5"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
						/>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
						/>
					</svg>
					<span class="hidden sm:inline">Click to view details</span>
					<span class="sm:hidden">View details</span>
				</span>
			</div>
		</div>

		<!-- Focus Ring for Accessibility -->
		<div
			class="absolute inset-0 rounded-xl ring-2 ring-blue-500 ring-offset-1 opacity-0 group-focus-within:opacity-100 transition-opacity duration-200 pointer-events-none"
		></div>
	</div>
</template>

<script>
export default {
	name: 'InvoiceItem',
	props: {
		invoice: {
			type: Object,
			required: true,
			default: () => ({
				createdDate: new Date(),
				invoiceStatus: 'PENDING-PAYMENT',
				invoiceCategory: 'Insurance Premium',
				invoiceCurrency: 'KES',
				invoiceAmount: 0,
				invoiceSystemRef: '',
				dueDate: null,
			}),
		},
	},
	methods: {
		getStatusClass(status) {
			const s = status && status.toLowerCase ? status.toLowerCase() : '';

			if (
				s.includes('paid') ||
				s.includes('complete') ||
				s.includes('approved')
			)
				return 'bg-green-50 text-green-700 border-green-200';
			if (
				s.includes('pending') ||
				s.includes('processing') ||
				s.includes('review')
			)
				return 'bg-red-50 text-blue-700 border-blue-200';
			if (
				s.includes('overdue') ||
				s.includes('failed') ||
				s.includes('rejected') ||
				s.includes('cancelled')
			)
				return 'bg-red-50 text-red-700 border-red-200';
			if (s.includes('draft') || s.includes('created'))
				return 'bg-gray-50 text-gray-700 border-gray-200';
			return 'bg-blue-50 text-blue-700 border-blue-200';
		},

		getStatusDotClass(status) {
			const s = status && status.toLowerCase ? status.toLowerCase() : '';

			if (
				s.includes('paid') ||
				s.includes('complete') ||
				s.includes('approved')
			)
				return 'bg-green-500';
			if (
				s.includes('pending') ||
				s.includes('processing') ||
				s.includes('review')
			)
				return 'bg-red-500';
			if (
				s.includes('overdue') ||
				s.includes('failed') ||
				s.includes('rejected') ||
				s.includes('cancelled')
			)
				return 'bg-red-500';
			if (s.includes('draft') || s.includes('created'))
				return 'bg-gray-500';
			return 'bg-blue-500';
		},

		getStatusIndicatorClass(status) {
			const s = status && status.toLowerCase ? status.toLowerCase() : '';

			if (
				s.includes('paid') ||
				s.includes('complete') ||
				s.includes('approved')
			)
				return 'bg-green-500';
			if (
				s.includes('pending') ||
				s.includes('processing') ||
				s.includes('review')
			)
				return 'bg-red-700';
			if (
				s.includes('overdue') ||
				s.includes('failed') ||
				s.includes('rejected') ||
				s.includes('cancelled')
			)
				return 'bg-red-500';
			if (s.includes('draft') || s.includes('created'))
				return 'bg-gray-500';
			return 'bg-blue-500';
		},

		formatAmount(amount) {
			if (typeof amount !== 'number') {
				const parsed = parseFloat(amount);
				return isNaN(parsed)
					? '0.00'
					: parsed.toLocaleString('en-US', {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2,
						});
			}
			return amount.toLocaleString('en-US', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			});
		},

		formatDate(dateString) {
			if (!dateString) return 'N/A';
			const date = new Date(dateString);
			return date.toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
			});
		},

		formatStatus(status) {
			const statusMap = {
				PAID: 'Paid',
				'PENDING-PAYMENT': 'Pending Payment',
				processing: 'Processing',
				pending: 'Pending',
				overdue: 'Overdue',
				failed: 'Failed',
				rejected: 'Rejected',
				cancelled: 'Cancelled',
				draft: 'Draft',
				created: 'Created',
			};
			return statusMap[status] || status;
		},

		formatStatusShort(status) {
			const statusMap = {
				PAID: 'Paid',
				'PENDING-PAYMENT': 'Pending',
				processing: 'Processing',
				pending: 'Pending',
				overdue: 'Overdue',
				failed: 'Failed',
				rejected: 'Rejected',
				cancelled: 'Cancelled',
				draft: 'Draft',
				created: 'Created',
			};
			return statusMap[status] || status;
		},

		truncateReference(ref) {
			if (!ref) return '';
			if (ref.length <= 12) return ref;
			return ref.substring(0, 6) + '...' + ref.substring(ref.length - 6);
		},
	},
};
</script>

<style scoped>
/* Focus styles for accessibility */
.group:focus {
	outline: none;
}

/* Enhanced hover effects */
.group:hover {
	transform: translateY(-2px);
	box-shadow:
		0 10px 25px -5px rgba(59, 130, 246, 0.1),
		0 4px 6px -2px rgba(59, 130, 246, 0.05);
}

/* High contrast mode support */
@media (prefers-contrast: high) {
	.border-blue-100,
	.border-gray-100,
	.border-gray-200 {
		border-color: #000;
	}

	.text-gray-900 {
		color: #000;
	}

	.text-gray-600,
	.text-gray-500,
	.text-gray-700 {
		color: #333;
	}

	.bg-blue-50,
	.bg-gray-50 {
		background: #f8f9fa !important;
	}
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
	.transition-all,
	.transition-opacity,
	.group:hover {
		transition: none !important;
		transform: none !important;
	}
}

/* Print styles */
@media print {
	.shadow-sm,
	.hover\:shadow-lg {
		box-shadow: none !important;
	}

	.bg-blue-50,
	.bg-gray-50 {
		background: #f8f9fa !important;
	}

	.group:hover {
		transform: none !important;
	}
}

/* Enhanced focus ring */
.group:focus-within .ring-2 {
	box-shadow:
		0 0 0 1px #ffffff,
		0 0 0 3px #3b82f6;
}

/* Custom breakpoint for extra small screens */
@media (min-width: 475px) {
	.xs\:inline {
		display: inline !important;
	}

	.xs\:hidden {
		display: none !important;
	}

	.xs\:flex-row {
		flex-direction: row !important;
	}

	.xs\:items-center {
		align-items: center !important;
	}

	.xs\:justify-between {
		justify-content: space-between !important;
	}
}

/* Mobile optimizations */
@media (max-width: 640px) {
	.text-base {
		font-size: 0.875rem;
	}

	.text-lg {
		font-size: 1.125rem;
	}

	.p-5 {
		padding: 1rem;
	}
}

/* Small mobile optimizations */
@media (max-width: 375px) {
	.p-4 {
		padding: 0.75rem;
	}

	.space-y-4 > * + * {
		margin-top: 0.75rem;
	}
}
</style>
